import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiEndpoints } from '../../utils/apiService';

/**
 * Group onboarding: every location's checklist, the intakes still open, and
 * starting or managing an intake link. Backed by
 * `GET /api/stores/store-groups/<id>/onboarding/` and the intake link
 * endpoints.
 */

export type ItemStatus = 'done' | 'todo' | 'waiting' | 'recommended' | 'locked';
export type StoreStage = 'not_started' | 'in_setup' | 'ready' | 'live';

export interface GroupStore {
  id: number;
  name: string;
  status: StoreStage;
  counts: { done: number; total: number; todo: number; waiting: number };
  next_step: { key: string; title: string } | null;
  live_at: string | null;
  items: { key: string; title: string; status: ItemStatus }[];
}

export type LinkState =
  | 'open'
  | 'submitted'
  | 'approved'
  | 'revoked'
  | 'expired';

export interface IntakeSummary {
  id: number;
  state: LinkState;
  /** The link, while it can still be filled in. */
  url: string;
  location_name: string;
  restaurant_name: string;
  store_group_id: number | null;
  store_group_name: string;
  copied_from_store_id: number | null;
  copied_from_name: string;
  same_legal_entity: boolean;
  confirmed: number;
  total: number;
  recipient_email: string;
  created_by_name: string;
  created_at: string;
  first_opened_at: string | null;
  last_activity_at: string | null;
  submitted_at: string | null;
  expires_at: string | null;
  store_id: number | null;
}

export interface GroupOnboarding {
  group: { id: number; name: string };
  stores: GroupStore[];
  intakes: IntakeSummary[];
}

export interface NewIntakeLink {
  location_name: string;
  store_group_id: number | null;
  copy_from_store_id: number | null;
  same_legal_entity: boolean;
  recipient_email: string;
}

async function readOrThrow<T>(response: Response | null): Promise<T> {
  const body = await response?.json().catch(() => null);
  if (!response?.ok) {
    const detail =
      body && typeof body === 'object'
        ? (body.detail ?? Object.values(body)[0])
        : null;
    throw new Error(
      typeof detail === 'string' ? detail : 'Something went wrong. Try again.',
    );
  }
  return body as T;
}

export const groupOnboardingKey = (groupId: number) =>
  ['group-onboarding', groupId] as const;

/**
 * Every store group, for a platform admin choosing whose onboarding to
 * open. `enabled` keeps it quiet for everyone else; group members only
 * ever see their own `user.onboarding_groups`.
 */
export function useStoreGroupOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['store-group-options'],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      type Row = { id: number; name: string };
      const body = await readOrThrow<Row[] | { results?: Row[] }>(
        await api.get(apiEndpoints.getStoreGroups()),
      );
      const rows = Array.isArray(body) ? body : (body.results ?? []);
      return rows.map(({ id, name }) => ({ id, name }));
    },
  });
}

export function useGroupOnboarding(groupId: number | null) {
  return useQuery({
    queryKey: groupOnboardingKey(groupId ?? 0),
    enabled: groupId != null,
    queryFn: async () =>
      readOrThrow<GroupOnboarding>(
        await api.get(apiEndpoints.groupOnboarding(groupId as number)),
      ),
  });
}

export function useCreateIntakeLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewIntakeLink) =>
      readOrThrow<IntakeSummary>(
        await api.post(apiEndpoints.createOnboardingLink(), input),
      ),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['group-onboarding'] }),
  });
}

export function useIntakeLinkAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: number;
      action: 'revoke' | 'new-link';
    }) =>
      readOrThrow<IntakeSummary>(
        await api.post(
          action === 'revoke'
            ? apiEndpoints.revokeOnboardingLink(id)
            : apiEndpoints.replaceOnboardingLink(id),
        ),
      ),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['group-onboarding'] }),
  });
}

/** The path of a link on this site, so "fill it in now" stays in the tab. */
export const linkPath = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};
